import React, { useRef, useState, useEffect } from 'react';
import { Button, Spin } from 'antd';
import s from './s.module.less';
import { EWSAction, TServerMessage, TWSMessage, TMessage } from 'types/inbox';
import InboxMessageList from 'pages/dashboard/InboxPage/MessageContainer/components/InboxMessageList';
import AiTextEditor from 'components/AI/AiTextEditor';
import { EAIEditorType } from 'types/ai';
import { handleServerSideMessage, unitAndSortServerMessages } from 'utils/inbox';
import {
    sendMessage,
    getConversationMessageList,
    TGetConversationMessageList,
    markReadForConversation,
} from 'api/inbox';
import { getUserId } from 'utils/localstore';
import { TWebSocketListener, useWebSocket } from 'hooks/inbox/useWebSocket';
import useProspectInfo from 'hooks/useProspectInfo';
import { CloseOutlined } from '@ant-design/icons';
import { EmptyChatIcon } from 'components/MenuIcons';
import useConversationInfo from 'hooks/useConversationInfo';
import FileTextArea, { FileTextAreaValue } from 'components/FileTextArea';

interface ChatBoxProps {
    patientId: number;
    onClose?: () => void;
    mode?: 'TEXT' | 'FILE';
    onNewMessage?: (name: string, value: TMessage) => void;
    fixTop?: boolean;
    fixBottom?: boolean;
    isGroupPractice?: boolean;
}

const DEFAULT_FETCH_MESSAGE_COUNT = 30;

const ChatBox: React.FC<ChatBoxProps> = ({
    patientId,
    onClose,
    mode = 'TEXT',
    onNewMessage,
    fixTop = false,
    fixBottom = false,
    isGroupPractice,
}) => {
    const conversationMessagList = React.useRef<TServerMessage[]>([]);
    const latestConversationMessageCursor = React.useRef<string>('');
    const allConversationMessageFetchedFlag = React.useRef<boolean>(false);

    const [loadingConversationMessage, setLoadingConversationMessage] = useState<boolean>(false);
    const loadingHistoryMessageRef = useRef<boolean>(false);

    const providerId = getUserId();

    const [targetClientDetail] = useProspectInfo(patientId, isGroupPractice);

    // 消息相关状态
    const [conversation, loadingConversationId] = useConversationInfo(getUserId(), patientId);
    const conversationIdRef = React.useRef<string | undefined>();
    const [messageList, setMessageList] = useState<TServerMessage[]>([]);
    const [displayMessageList, setDisplayMessageList] = useState<TMessage[]>([]);
    const [newMsgValue, setNewMsgValue] = useState<FileTextAreaValue>({ text: '', files: [] });
    const [replyingMsg, setReplyingMsg] = useState<boolean>(false);
    const [resetFlag, setResetFlag] = useState<{}>();
    const [clearMessageFlag, setClearMessageFlag] = useState<{}>();

    // 使用 ref 存储最新的 targetClientDetail，确保 WebSocket 回调能访问到最新值
    const targetClientDetailRef = React.useRef(targetClientDetail);
    React.useEffect(() => {
        targetClientDetailRef.current = targetClientDetail;
    }, [targetClientDetail]);

    const isEmpty = messageList.length === 0;
    const getPatientFullName = () => {
        // 使用 ref 获取最新的值
        const currentClient = targetClientDetailRef.current;
        if (currentClient?.firstName && currentClient?.lastName) {
            return `${currentClient.firstName} ${currentClient.lastName}`;
        }
        return 'Patient';
    };

    const updateDisplayMessageList = (serverMessages: TServerMessage[], isFromWs = false) => {
        const displayMessages = handleServerSideMessage(serverMessages, 0);
        if (isFromWs && displayMessages?.[displayMessages?.length - 1]) {
            onNewMessage?.(getPatientFullName(), displayMessages[displayMessages?.length - 1]);
        }
        setDisplayMessageList(displayMessages || []);
    };

    // 使用 ref 存储最新的 updateDisplayMessageList 函数，确保 WebSocket 回调能访问到最新版本
    const updateDisplayMessageListRef = React.useRef(updateDisplayMessageList);
    React.useEffect(() => {
        updateDisplayMessageListRef.current = updateDisplayMessageList;
    }, [updateDisplayMessageList]);

    // WebSocket监听
    const webSocketOptions: TWebSocketListener = {
        onMessage: (event: MessageEvent) => {
            try {
                const data: TWSMessage = JSON.parse(event.data);
                if (data.action === EWSAction.NEWMSG && data.payload) {
                    const serverMessage = data.payload;
                    if (serverMessage.conversationId === conversationIdRef.current) {
                        const cacheMessageList = conversationMessagList.current || [];
                        const _message = unitAndSortServerMessages([...cacheMessageList, serverMessage]);
                        conversationMessagList.current = _message || [];
                        setMessageList(_message || []);
                        updateDisplayMessageListRef.current(_message || [], true);
                        scrollToLatestMessage(100, true);
                    }
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        },
    };

    useWebSocket(webSocketOptions);

    const scrollToLatestMessage = (timeout?: number, smooth?: boolean) => {
        setTimeout(() => {
            const messageWrap = document.getElementById('chatBoxMessageWrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: 99999,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, timeout || 150);
    };

    const onClickReply = async () => {
        if (replyingMsg) {
            return;
        }
        if (!targetClientDetail?.id || (!newMsgValue.text.trim() && newMsgValue.files.length === 0)) {
            return;
        }
        const message = newMsgValue.text.trim() ? newMsgValue.text.replace(/<[^>]+>/g, '') : '';
        const files = newMsgValue.files.map((file) => file.id);

        try {
            setReplyingMsg(true);
            if (message) {
                const res = await sendMessage({
                    providerId: providerId!,
                    clientId: targetClientDetail?.id! || patientId!,
                    message,
                });

                if (!res.error) {
                    setResetFlag({});
                    setClearMessageFlag({});

                    const messageData: TServerMessage = res.data?.data;
                    const cacheMessageList = conversationMessagList.current || [];
                    const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
                    conversationMessagList.current = _message || [];
                    setMessageList(_message || []);
                    updateDisplayMessageList(_message || []);
                }
            }
            if (files.length > 0) {
                const res = await Promise.all(
                    newMsgValue.files.map(async (file) => {
                        const resFile = await sendMessage({
                            providerId: providerId!,
                            clientId: targetClientDetail?.id! || patientId!,
                            fileId: file.id,
                        });
                        if (!resFile.error) {
                            return resFile.data.data;
                        }
                        return null;
                    }),
                );
                res.forEach((newMsg) => {
                    if (newMsg) {
                        const cacheMessageList = conversationMessagList.current || [];
                        const _message = unitAndSortServerMessages([...(cacheMessageList || []), newMsg]);
                        conversationMessagList.current = _message || [];
                        setMessageList(_message || []);
                        updateDisplayMessageList(_message || []);
                    }
                });
            }
            setNewMsgValue({ text: '', files: [] });
            scrollToLatestMessage();
        } catch (error) {
            console.error('error:', error);
        } finally {
            setReplyingMsg(false);
        }
    };

    const fetchMessageByConversationId = async (_cid: string, cursor?: string, size?: number) => {
        const param: TGetConversationMessageList = {
            conversationId: _cid,
            size: size || DEFAULT_FETCH_MESSAGE_COUNT,
        };
        if (cursor) {
            param.cursor = cursor;
        }
        // 只有在加载历史消息时才不设置 loading（因为已经有消息显示了）
        if (!cursor) {
            setLoadingConversationMessage(true);
        }
        const res = await getConversationMessageList(param);
        if (!res.error) {
            const data = res?.data?.data?.items || [];
            const _cursor = res?.data?.data?.cursor;

            if (_cursor || !cursor) {
                latestConversationMessageCursor.current = _cursor || '';
            }

            if (data.length === 0) {
                allConversationMessageFetchedFlag.current = true;
            }

            let _message: TServerMessage[] = [];

            if (cursor) {
                const cacheMessageList = conversationMessagList.current || [];
                _message = unitAndSortServerMessages([...data, ...cacheMessageList]) || [];
            } else {
                _message = unitAndSortServerMessages([...data]) || [];
            }

            conversationMessagList.current = _message;
            setMessageList(_message);
            updateDisplayMessageList(_message);

            if (!cursor) {
                scrollToLatestMessage(200);
            }
        }
        setLoadingConversationMessage(false);
    };

    const loadHistoryMessage = async () => {
        if (loadingHistoryMessageRef.current === true) {
            return;
        }
        const targetConversationId = conversationIdRef.current;
        const cursor = latestConversationMessageCursor.current;
        if (!targetConversationId || !cursor || allConversationMessageFetchedFlag.current) {
            return;
        }

        try {
            loadingHistoryMessageRef.current = true;
            await fetchMessageByConversationId(targetConversationId, cursor);
            loadingHistoryMessageRef.current = false;
        } catch (error) {
            console.error('error:', error);
        } finally {
            loadingHistoryMessageRef.current = false;
        }
    };

    const getPatientInitials = () => {
        if (targetClientDetail?.firstName && targetClientDetail?.lastName) {
            return `${targetClientDetail.firstName.charAt(0)}${targetClientDetail.lastName.charAt(0)}`;
        }
        return 'P';
    };

    // 当 patientId 变化时，清空所有消息相关的状态和缓存，并显示 loading
    useEffect(() => {
        conversationMessagList.current = [];
        latestConversationMessageCursor.current = '';
        allConversationMessageFetchedFlag.current = false;
        conversationIdRef.current = undefined;
        loadingHistoryMessageRef.current = false;

        setMessageList([]);
        setDisplayMessageList([]);
        setNewMsgValue({ text: '', files: [] });
        setReplyingMsg(false);
        setResetFlag({});
        setClearMessageFlag({});

        // 立即显示 loading 状态，避免闪烁
        setLoadingConversationMessage(true);
    }, [patientId]);

    useEffect(() => {
        if (!conversation || !conversation.conversationId) {
            return;
        }
        conversationIdRef.current = conversation?.conversationId;
        fetchMessageByConversationId(conversation.conversationId);
    }, [conversation]);

    useEffect(() => {
        if (isEmpty) {
            return;
        }
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop < 100) {
                loadHistoryMessage();
            }
        };

        const messageWrap = document.getElementById('chatBoxMessageWrap');
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }
        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [isEmpty]);

    const renderMessageContent = () => {
        if (loadingConversationMessage && isEmpty) {
            return (
                <div className={s.messageWrap} id="chatBoxMessageWrap">
                    <Spin className={s.messageLoading} />
                </div>
            );
        }
        if (isEmpty) {
            return (
                <div className={s.emptyWrap}>
                    {/* {EmptyChatIcon} */}
                    <div className={s.emptyTitle}>No messages yet</div>
                    <div className={s.emptyDes}>Start chatting with the patient anytime!</div>
                </div>
            );
        }
        return (
            <div className={s.messageWrap} id="chatBoxMessageWrap">
                {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                <InboxMessageList
                    inboxMessageList={displayMessageList}
                    inboxConversation={{
                        conversationId: conversation?.conversationId || '',
                        clientId: patientId,
                        providerId: providerId!,
                        lastMessageId: '',
                        lastMessageContent: '',
                        lastMessageAt: '',
                        lastMessageType: messageList[messageList.length - 1]?.messageType,
                        lastMessageSenderType: messageList[messageList.length - 1]?.senderType,
                        unreadCount: 0,
                        status: 'ACTIVE',
                        pinned: false,
                        pinTime: null,
                        lastReadAt: null,
                        createdAt: '',
                        patientFirstName: targetClientDetail?.firstName || 'Patient',
                        patientLastName: targetClientDetail?.lastName || '',
                        providerFirstName: '',
                        providerLastName: '',
                        providerAvatarUrl: null,
                    }}
                    blockOpenTextVisitModal
                />
            </div>
        );
    };

    return (
        <div className={`${s.chatBox} ${fixTop ? s.chatBoxFixTop : ''} ${fixBottom ? s.chatBoxFixBottom : ''}`}>
            <div className={s.header}>
                <div className={s.cName}>
                    <span className={s.icon}>{getPatientInitials()}</span>
                    {getPatientFullName()}
                </div>
                <CloseOutlined onClick={onClose} />
            </div>

            {renderMessageContent()}

            <div className={s.messageInput}>
                {mode === 'FILE' ? (
                    <FileTextArea value={newMsgValue} onChange={setNewMsgValue} onSend={onClickReply} />
                ) : (
                    <>
                        <div className={s.aiWrap}>
                            <AiTextEditor
                                rows={3}
                                onChange={(e) => {
                                    if (typeof e === 'string') {
                                        setNewMsgValue({ ...newMsgValue, text: e as string });
                                    } else {
                                        setNewMsgValue({ ...newMsgValue, text: e ? e.target.value : '' });
                                    }
                                }}
                                clearMessageFlag={clearMessageFlag}
                                value={newMsgValue.text}
                                type={EAIEditorType.PROSPECT}
                                resetFlag={resetFlag}
                                maxLength={3000}
                                narrow={false}
                                size="small"
                            />
                        </div>

                        <div className={s.btnWrap}>
                            <Button
                                className={s.btn}
                                loading={replyingMsg}
                                onClick={onClickReply}
                                type="primary"
                                disabled={!newMsgValue.text.trim() || replyingMsg}
                            >
                                Send
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChatBox;
